import cloneDeep from 'clone-deep'
import { Frequency } from 'components/Frequency'
import { RepeatType } from 'components/Frequency/constants'
import { validateFrequencyData } from 'components/Frequency/logic'
import { LoanSearchField } from 'components/GlobalLoanSearch/LoanSearchField'
import { getLoanSearchParts } from 'components/GlobalLoanSearch/utils'
import { PayeeSearch } from 'components/PayeeSearch'
import { type InputType, SettingKey } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { AccountingVendors, AddVendor } from 'pages/Admin/Accounting/AccountingVendors'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { createAccountingVendor, extractInvoiceFromPdf, getAccountingVendorCategories, getAdminConfig } from 'services'
import { Modal } from 'stories/components'
import {
  confirm,
  formatDate,
  InputConvert,
  InputValidate,
  openAuditLog,
  removeComma,
  thousandSeperator,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import type { IInvoice } from './types'

export const defaultInputs = (isLoan: boolean): Record<string, InputType> => {
  const loanNumber = setLoanNumber()
  const rtl: Record<string, InputType> = {
    loanNumber: {
      inputType: 'text',
      type: 'number',
      title: 'Loan number',
      required: false,
      span: 'full',
      history: true,
    },
    date: {
      inputType: 'text',
      type: 'date',
      title: 'Invoice Date',
      required: true,
      history: true,
    },
    payee: {
      inputType: 'text',
      type: 'text',
      title: 'Payee',
      required: true,
      history: true,
    },
    description: {
      inputType: 'textarea',
      type: 'text',
      title: 'Description',
      value: '',
      span: 'full',
      required: false,
      history: true,
    },
    no: {
      inputType: 'text',
      title: 'Invoice Number',
      required: true,
      history: true,
    },
    amount: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Amount',
      prefix: '$',
      required: true,
      history: true,
    },
    addLoanCharge: {
      inputType: 'checkbox',
      title: 'Add as Loan Charge',
      value: true,
      history: true,
    },
    dueDate: {
      inputType: 'text',
      type: 'date',
      title: 'Due Date',
      required: false,
      history: true,
    },
    interestRate: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Interest Rate',
      prefix: '%',
      required: true,
      history: true,
    },
    isPaid: {
      inputType: 'buttongroupselector',
      title: 'Paid',
      options: ['Not Paid', 'Paid', 'Rejected'],
      span: 'full',
      required: true,
      history: true,
    },
    paymentMethod: {
      inputType: 'select',
      title: 'Payment Method',
      required: true,
      hasDefaultOption: true,
      options: {},
      sort: true,
      history: true,
    },
    paymentDate: {
      inputType: 'text',
      type: 'date',
      title: 'Payment Date',
      required: true,
      history: true,
    },
    files: {
      inputType: 'FileTable',
      title: 'File',
      required: false,
      acceptFileTypes: '.pdf',
      filePath: `loan/${loanNumber}/invoices`,
      span: 'full',
      showCategory: false,
      showStatus: false,
      history: true,
    },
  }
  if (isLoan) delete rtl.loanNumber
  return rtl
}

type InvoiceAiExtractData = {
  no?: string | null
  loanNumber?: number | null
  date?: string | null
  payee?: string | null
  description?: string | null
  amount?: number | null
}

export const AddInvoice = ({
  index,
  item,
  onClose,
  onUpdate,
  isLoan,
  accountingAccounts,
  invoiceNos,
  defaultValues,
}: {
  index: number
  item: IInvoice | null
  onClose: Function
  onUpdate: Function
  isLoan: boolean
  accountingAccounts: Record<number, any>
  invoiceNos: Array<Record<string, any>>
  defaultValues?: Partial<IInvoice>
}) => {
  const [loading, setLoading] = useState(false)
  const [aiRunning, setAiRunning] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs(isLoan))
  const [showVendor, setShowVendor] = useState(false)
  const [newVendorName, setNewVendorName] = useState<string | null>(null)
  const [vendorCategories, setVendorCategories] = useState<Record<number, string>>({})
  const [aiConfigEnabled, setAiConfigEnabled] = useState(false)
  const [frequency, setFrequency] = useState<Record<string, any>>({ repeatType: RepeatType.NoRepeat })
  const [errorCheck, setErrorCheck] = useState(false)

  const { hasPermission } = usePermissions()
  const canAiExtractPermission = hasPermission('Loan.Servicing.Invoice.AIExtract')
  const canAiExtractRef = useRef(false)
  canAiExtractRef.current = !!canAiExtractPermission && aiConfigEnabled
  const isLoanRef = useRef(isLoan)
  isLoanRef.current = isLoan

  useEffect(() => {
    getAccountingVendorCategories().then((res) => {
      const next: Record<number, string> = {}
      ;(res || []).forEach((item: any) => {
        next[item.id] = item.value
      })
      setVendorCategories(next)
    })
  }, [])

  useEffect(() => {
    if (!canAiExtractPermission) {
      setAiConfigEnabled(false)
      return
    }
    getAdminConfig(SettingKey.GLOBAL_SETTINGS).then((settings: any) => {
      setAiConfigEnabled(!!settings?.invoiceAiAutofillEnabled)
    })
  }, [canAiExtractPermission])

  useEffect(() => {
    const newInputs: any = cloneDeep(inputs)
    const source = item || defaultValues
    if (source) {
      Object.keys(inputs).forEach((key) => {
        if ((source as any)[key] === undefined) return
        if ((key == 'date' || key == 'dueDate') && (source as any)[key])
          (source as any)[key] = `${(source as any)[key]}`.substring(0, 10)
        newInputs[key].value = (source as any)[key]
        if (key === 'loanNumber')
          newInputs[key].value = (source as any)['byteproFileName'] || (source as any)['loanNumber']
      })
      const loanNumber = (source as any)['loanNumber']
      if (loanNumber) (newInputs.files as any).filePath = `loan/${loanNumber}/invoices`
    }
    newInputs.paymentMethod.options = accountingAccounts
    setInputs(newInputs)
    if (source && (source as any).frequency) {
      setFrequency((source as any).frequency || { repeatType: RepeatType.NoRepeat })
    }
  }, [])

  const applyAiExtract = (nextInputs: Record<string, InputType>, data: InvoiceAiExtractData) => {
    if (!isLoanRef.current && data.loanNumber != null && nextInputs.loanNumber) {
      nextInputs.loanNumber.value = data.loanNumber
      nextInputs.loanNumber.error = InputValidate({ ...nextInputs.loanNumber, value: data.loanNumber })
      ;(nextInputs.files as any).filePath = `loan/${data.loanNumber}/invoices`
    }
    if (data.no != null && nextInputs.no) {
      nextInputs.no.value = data.no
      nextInputs.no.error = InputValidate({ ...nextInputs.no, value: data.no })
    }
    if (data.date != null && nextInputs.date) {
      nextInputs.date.value = data.date
      nextInputs.date.error = InputValidate({ ...nextInputs.date, value: data.date })
    }
    if (data.payee != null && nextInputs.payee) {
      nextInputs.payee.value = data.payee
      nextInputs.payee.error = InputValidate({ ...nextInputs.payee, value: data.payee })
    }
    if (data.description != null && nextInputs.description) {
      nextInputs.description.value = data.description
      nextInputs.description.error = InputValidate({ ...nextInputs.description, value: data.description })
    }
    if (data.amount != null && nextInputs.amount) {
      nextInputs.amount.value = data.amount
      nextInputs.amount.error = InputValidate({ ...nextInputs.amount, value: data.amount })
    }
  }

  const onChange = async (key: string, value: any, filePathLoanNo?: number | string) => {
    let newInputs = cloneDeep(inputs)
    const previousFiles: Array<{ fileKey?: string }> = Array.isArray(inputs.files?.value) ? inputs.files.value : []
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value

    if (key === 'loanNumber') {
      const pathNo = filePathLoanNo || value
      ;(newInputs.files as any).filePath = `loan/${pathNo}/invoices`
    }
    setInputs(newInputs)

    if (key !== 'files' || !canAiExtractRef.current) return

    const nextFiles: Array<{ fileKey?: string; name?: string }> = Array.isArray(value) ? value : []
    const previousKeys = new Set(previousFiles.map((file) => file.fileKey).filter(Boolean))
    const addedFiles = nextFiles.filter((file) => file.fileKey && !previousKeys.has(file.fileKey))
    if (!addedFiles.length) return

    const fileToExtract = addedFiles[addedFiles.length - 1]
    setAiRunning(true)
    setLoading(true)
    try {
      const res = await extractInvoiceFromPdf([
        { fileKey: String(fileToExtract.fileKey), name: String(fileToExtract.name || 'invoice.pdf') },
      ])
      if (res?.success && res.data) {
        setInputs((current) => {
          const updated = cloneDeep(current)
          applyAiExtract(updated, res.data)
          return updated
        })
      } else if (res?.message) {
        toast(res.message, { type: 'error' })
      } else {
        toast('Failed to extract invoice fields from the document.', { type: 'error' })
      }
    } catch {
      toast('Failed to extract invoice fields from the document.', { type: 'error' })
    } finally {
      setAiRunning(false)
      setLoading(false)
    }
  }

  const showHistory = (key: string) => {
    if (!item?.id) return
    const input = inputs[key]
    openAuditLog({
      table: 'Invoice',
      field: key,
      fieldLabel: input?.title || key,
      keys: {
        id: item.id,
        field: key,
      },
      map: input?.options,
    })
  }

  const onSubmit = async () => {
    let hasError = false

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      if (['paymentMethod', 'paymentDate'].includes(key) && inputs.isPaid.value != 'Paid') continue
      if (key === 'interestRate' && !inputs.addLoanCharge.value) continue
      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    const freqError = validateFrequencyData(frequency)
    setErrorCheck(true)
    if (hasError || freqError) return

    data.amount = removeComma(data.amount)
    if (isLoan) data.loanNumber = setLoanNumber()
    data.loanNumber = Number(data.loanNumber)
    if (!data.loanNumber) data.loanNumber = null
    if (!data.dueDate) data.dueDate = null
    if (data.isPaid === 'Paid') data.approved = true
    data.frequency = frequency
    let existingIdx = -1
    if (item && item.no == data.no && item.payee == data.payee) {
    } else existingIdx = invoiceNos.findIndex(({ no, payee }) => no === data.no && payee === data.payee)
    if (existingIdx !== -1) {
      const isContinue = await confirm(
        `This is a potential duplicate of No.${existingIdx + 1}  created At ${formatDate(
          invoiceNos[existingIdx].createdAt,
        )}.`,
        { titleYes: `Proceed and ${item ? 'Update' : 'Add'}`, titleNo: 'Cancel' },
      )
      if (!isContinue) return
    }
    // Marking an invoice paid by hand does not touch a payment already raised from it: that one is
    // still in the check queue and will be cut. Say so rather than letting the two records drift.
    const queued = Number(item?.queuedPayableAmount) || 0
    const previouslyPaid = item?.isPaid === true || String(item?.isPaid) === 'Paid'
    if (item && queued > 0 && data.isPaid === 'Paid' && !previouslyPaid) {
      const isContinue = await confirm(
        `A payment of $${thousandSeperator(queued)} raised from this invoice is still in the check queue.` +
          ` Marking the invoice paid here does not pay or remove it.`,
        { titleYes: 'Mark it paid', titleNo: 'Cancel' },
      )
      if (!isContinue) return
    }
    setLoading(true)
    onUpdate(data)
  }

  return (
    <>
      <Modal
        title={item ? `Update Invoice - No.${index + 1}` : 'Add Invoice'}
        titleOkay="Submit"
        loading={loading}
        loadingMessage={aiRunning ? 'AI Is Running' : undefined}
        onClose={() => onClose()}
        onOk={onSubmit}
        isOpen
      >
        <div className="text-gray-900">
          <div className="w-144 grid gap-4 md:grid-cols-2 grid-cols-1 mb-3 mt-2">
            {Object.keys(inputs).map((key, index) => {
              if (['paymentMethod', 'paymentDate'].includes(key) && inputs.isPaid.value != 'Paid') return
              if (key === 'interestRate' && !inputs.addLoanCharge.value) return
              let input = inputs[key]
              const canShowHistory = !!item?.id
              if (key === 'payee')
                return (
                  <div className={`input md:col-span-${input.span || 1}`} key={index}>
                    <PayeeSearch
                      value={String(input.value || '')}
                      title={String(input.title || 'Payee')}
                      required={!!input.required}
                      error={String(input.error || '')}
                      history={canShowHistory}
                      showHistory={() => showHistory('payee')}
                      additionalElements={
                        <span
                          className="text-shade-blue cursor-pointer hover:underline"
                          onClick={() => setShowVendor(true)}
                        >
                          Select
                        </span>
                      }
                      onChange={(value) => onChange('payee', value)}
                      onAddNew={(name) => setNewVendorName(name)}
                    />
                  </div>
                )
              if (key === 'loanNumber') {
                return (
                  <div className={`input md:col-span-${input.span || 1}`} key={index}>
                    <LoanSearchField
                      value={String(input.value || '')}
                      title={String(input.title || 'Loan number')}
                      required={!!input.required}
                      error={String(input.error || '')}
                      history={canShowHistory}
                      showHistory={canShowHistory ? () => showHistory('loanNumber') : undefined}
                      onChange={(value) => onChange('loanNumber', value)}
                      onSelectLoan={(loan) => {
                        const { loanNo } = getLoanSearchParts(loan)
                        onChange('loanNumber', String(loanNo || ''), loan.no)
                      }}
                    />
                  </div>
                )
              }
              return (
                <div className={`input md:col-span-${input.span || 1}`} key={index}>
                  <RenderInput
                    input={canShowHistory ? input : { ...input, history: false }}
                    Key={key}
                    onChange={onChange}
                    showHistory={canShowHistory ? showHistory : undefined}
                  />
                </div>
              )
            })}
          </div>
          <div className="mt-2 mb-2">
            <Frequency
              data={frequency}
              startDate={String(inputs.date?.value || '')}
              onChange={(value: any) => setFrequency(value)}
              errorCheck={errorCheck}
            />
          </div>
        </div>
      </Modal>
      {showVendor && (
        <Modal
          title="Select Payee"
          titleOkay=""
          onClose={() => setShowVendor(false)}
          onOk={() => {}}
          childLevel={2}
          isOpen
        >
          <div className="w-196">
            <AccountingVendors
              forSearch={true}
              onSelect={(name: string) => {
                onChange('payee', name), setShowVendor(false)
              }}
            />
          </div>
        </Modal>
      )}
      {newVendorName !== null && (
        <AddVendor
          index={-1}
          item={null}
          categories={vendorCategories}
          initialName={newVendorName}
          childLevel={2}
          onClose={() => setNewVendorName(null)}
          onUpdate={async (data: Record<string, any>) => {
            const vendor = await createAccountingVendor(data)
            setNewVendorName(null)
            onChange('payee', vendor.name)
          }}
        />
      )}
    </>
  )
}
